import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-procedure-details-modal',
    standalone: true,
    imports: [CommonModule, TranslateModule],
    template: `
    <div class="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" (click)="close.emit()"></div>

      <!-- Modal Content -->
      <div class="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-slide-up flex flex-col max-h-[90vh]">
        
        <!-- Header -->
        <div class="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
          <div>
            <h3 class="text-xl font-black text-slate-800 uppercase tracking-widest" id="modal-title">
              {{ 'modal_procedure_details_title' | translate }}
            </h3>
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{{ 'modal_procedure_details_subtitle' | translate }}</p>
          </div>
          <button (click)="close.emit()" class="p-2 hover:bg-slate-50 rounded-xl transition-colors text-slate-400 hover:text-slate-600">
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto p-8 space-y-8">
          <div class="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
            <span class="text-[10px] font-black text-slate-800 uppercase tracking-widest">{{ 'modal_procedure_details_status_label' | translate }}</span>
            <span class="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm" [ngClass]="{
                'status-success': procedure?.active === true,
                'bg-slate-100 text-slate-400 border border-slate-200': procedure?.active !== true
              }">
              {{ (procedure?.active === true ? 'status_active' : 'status_inactive') | translate }}
            </span>
          </div>

          <div class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_procedure_family' | translate }}</label>
                <p class="text-sm font-bold text-slate-700">{{ procedure?.family || '-' }}</p>
              </div>

              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_procedure_subfamily' | translate }}</label>
                <p class="text-sm font-bold text-slate-700">{{ procedure?.subfamily || '-' }}</p>
              </div>

              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_procedure_group' | translate }}</label>
                <p class="text-sm font-bold text-slate-700">{{ procedure?.group || '-' }}</p>
              </div>

              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_procedure_subgroup' | translate }}</label>
                <p class="text-sm font-bold text-slate-700">{{ procedure?.subgroup || '-' }}</p>
              </div>

              <div class="col-span-2">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'PROCEDURE' | translate }}</label>
                <p class="text-sm font-bold text-slate-700 leading-relaxed">{{ procedure?.description || '-' }}</p>
              </div>

              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_procedure_specialty' | translate }}</label>
                <p class="text-sm font-bold text-slate-700">{{ procedure?.subfamily || '-' }}</p>
              </div>

              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_procedure_amount' | translate }}</label>
                <p class="text-sm font-black text-primary">{{ procedure?.base_price | currency:'EUR':'symbol':'1.0-0' }}</p>
              </div>
              
              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'tax_vat' | translate }} (%)</label>
                @let ivaVal = procedure?.tax_data?.value != null ? procedure.tax_data.value : (procedure?.iva || 0);
                <p class="text-sm font-bold text-slate-700">
                  {{ ivaVal > 0 ? ivaVal + '%' : (procedure?.exemption_data?.code ? '0' : '-') }}
                  <span *ngIf="ivaVal == 0 && procedure?.exemption_data?.code"> ({{ procedure.exemption_data.code }})</span>
                </p>
              </div>

              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'created_by_label' | translate }}</label>
                <p class="text-[10px] font-bold text-slate-500 uppercase">{{ procedure?.created_by || '-' }}</p>
              </div>

              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'updated_by_label' | translate }}</label>
                <p class="text-[10px] font-bold text-slate-500 uppercase">{{ procedure?.updated_by || '-' }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-6 border-t border-slate-100 bg-white">
          <button (click)="close.emit()" class="w-full py-4 bg-slate-50 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-all">
            {{ 'button_cancel' | translate }}
          </button>
        </div>
      </div>
    </div>
  `
})
export class ProcedureDetailsModal {
    @Input() procedure: any;
    @Output() close = new EventEmitter<void>();
}
